import { EditOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Collapse,
  message,
  Modal,
  Table,
  Typography
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { SessionContext } from "../../App";
import EditEmailTemplateForm from "../../components/EditEmailTemplate";
import {
  GetEmailTemplateByCompanyIdResponse,
  GetSurveyCampaignBySurveyCampaignIdResponse,
  GetSurveyResponseBySurveyCampaignIdResponse,
  UpdateEmailTemplateRequest,
} from "../../constants/apiRequestResponse";
import TEST_IDS from "../../constants/cypressTestIds";
import { COLORS } from "../../constants/misc";
import { EmailTemplate } from "../../constants/types";
import {
  fetcher,
  startSurveyCampaign,
  updateEmailTemplate,
} from "../../services/api";
import { EditEmailTemplatePageKeys } from "../../services/i8tn/EditEmailTemplatePage/keys";
import { SurveyCampaignFormKeys } from "../../services/i8tn/SurveyCampaignForm/keys";
import { getQueryParam, stringParser } from "../../util";

type EmailInputParams = {
  currentUserName?: string;
  companyName?: string;
  surveyCampaignName?: string;
  link?: string;
  email?: string;
};
export default function Preview360() {
  const context = useContext(SessionContext);

  const history = useHistory();

  const surveyCampaignId = getQueryParam("surveyCampaignId") || "";

  const { data: getSurveyResponseRes, isLoading: isSurveyResponseLoading } =
    useSWR<GetSurveyResponseBySurveyCampaignIdResponse>(
      `/surveyResponse/getBySurveyCampaignId/${surveyCampaignId}`,
      fetcher,
      {
        errorRetryCount: 0,
      }
    );

  const { data: getSurveyCampaignRes } =
    useSWR<GetSurveyCampaignBySurveyCampaignIdResponse>(
      `/surveyCampaign/getBySurveyCampaignId/${getQueryParam(
        "surveyCampaignId"
      )}`,
      fetcher,
      {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        errorRetryCount: 0,
      }
    );

  const { data: getEmailTemplateRes } =
    useSWR<GetEmailTemplateByCompanyIdResponse>(
      `/emailTemplate/get/${context?.companyUser?.companyId}`,
      fetcher,
      {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        errorRetryCount: 0,
      }
    );

  // just to warm up cache, such that if user attempts to edit email template it'll load faster
  useSWR(
    `/emailTemplate/variables/get`,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      errorRetryCount: 0,
    }
  );

  const [emailTemplateByPosition, setEmailTemplateByPosition] = useState<
    {[key:string]: EmailTemplate}
  >({});
  const [isUpdatingEmailTemplateLoadingByEmailTemplateId, setIsUpdatingEmailTemplateLoadingByEmailTemplateId] = useState<
    {[key:string]: boolean}
  >({});
  const [isSaveAsDraftLoading, setIsSaveAsDraftLoading] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [isEditEmailTemplateByEmailTemplateId, setIsEditEmailTemplateByEmailTemplateId] = useState<
    {[key:string]: boolean}
  >({});
  const [editorStateByEmailTemplateId, setEditorStateByEmailTemplateId] = useState<
    {[key:string]: string}
  >({});

  const { t } = useTranslation();

  useEffect(() => {
    const newEmailTemplateByPosition:{[key:string]: EmailTemplate} = {}

    getEmailTemplateRes?.emailTemplates?.forEach(et => {
      if(et.surveyTemplateId !== getSurveyCampaignRes?.surveyCampaign?.surveyTemplateId) return
      if(!et.position) return

      const inputParams: EmailInputParams = {
        companyName: `${context?.company?.name}`,
        surveyCampaignName: getSurveyCampaignRes?.surveyCampaign?.name,
        email: context?.company?.helpEmail,
      };

      et.content = stringParser(
        et?.content || "",
        inputParams
      );

      newEmailTemplateByPosition[et.position] = et;
    })

    setEmailTemplateByPosition(newEmailTemplateByPosition);
  }, [
    getEmailTemplateRes,
    getSurveyCampaignRes,
    context?.company,
  ]);

  const isEditingAtLeastOneEmailTemplate = Object.values(isEditEmailTemplateByEmailTemplateId).find(
    (isEditing) => isEditing === true
  );

  return (
    <div>
      <Typography.Title
        data-testid={TEST_IDS.SURVEY_CAMPAIGN_FORM.preview_and_send_label_id}
        level={3}
      >
        {t(SurveyCampaignFormKeys.surveyCampaignForm_previewAndSendTitleString)}
      </Typography.Title>
      <Table
        loading={isSurveyResponseLoading}
        rowKey="_id"
        size="small"
        columns={[
          {
            title: (
              <>
                {t(
                  SurveyCampaignFormKeys.surveyCampaignForm_previewFirstNameTableHeader
                )}
              </>
            ),
            dataIndex: "fname",
          },
          {
            title: (
              <>
                {t(
                  SurveyCampaignFormKeys.surveyCampaignForm_previewLastNameTableHeader
                )}
              </>
            ),
            dataIndex: "lname",
          },
          {
            title: (
              <>
                {t(
                  SurveyCampaignFormKeys.surveyCampaignForm_previewEmailTableHeader
                )}
              </>
            ),
            dataIndex: "email",
          },
          {
            title: (
              <>
                {t(
                  SurveyCampaignFormKeys.surveyCampaignForm_previewPositionTableHeader
                )}
              </>
            ),
            dataIndex: "position",
          },
          {
            title: (
              <>
                {t(
                  SurveyCampaignFormKeys.surveyCampaignForm_previewTeamTableHeader
                )}
              </>
            ),
            dataIndex: "team",
          },
          {
            title: (
              <>
                {t(
                  SurveyCampaignFormKeys.surveyCampaignForm_previewGenderTableHeader
                )}
              </>
            ),
            dataIndex: "gender",
          },
        ]}
        dataSource={getSurveyResponseRes?.surveyResponses.map((sr) => {
          return sr.surveyEmployee;
        })}
        pagination={false}
        scroll={{
          x: true,
        }}
      />

      <br />

      {
        Object.keys(emailTemplateByPosition).map(position => {
          const emailTemplate = emailTemplateByPosition[position];
          if (!emailTemplate) return null;

          const isEditing = isEditEmailTemplateByEmailTemplateId[emailTemplate._id];

          return (
            <div
              key={position}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                paddingBottom: 10,
                flexDirection: "column",
              }}
            >
                  
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexDirection: "row",
                }}
              >
                <h4>
                  {t(
                    SurveyCampaignFormKeys.surveyCampaignForm_previewExampleEmailString
                  )} {position}
                </h4>

                  {
                    !isEditing ? (
                      <EditOutlined
                        style={{
                          color: COLORS.PRIMARY_COLOR,
                          fontSize: 18,
                        }}
                        data-testid={`${TEST_IDS.SURVEY_CAMPAIGN_FORM.edit_email_template_id}_${position}`}
                        onClick={() => {
                          setIsEditEmailTemplateByEmailTemplateId(prev => {
                            const now = JSON.parse(JSON.stringify(prev));
                            now[emailTemplate._id] = true
                            return now
                          });
                        }}
                      />
                    ):null
                  }
              </div>
              <Card>
                { isEditing ? (
                  <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
                    <EditEmailTemplateForm
                      emailTemplateId={emailTemplate._id}
                      isShowFooter={false}
                      onUpdate={(_editorState) => {
                        setEditorStateByEmailTemplateId(prev => {
                          if(!_editorState) return prev
                          const now = JSON.parse(JSON.stringify(prev));
                          now[emailTemplate._id] = _editorState
                          return now
                        })
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                      }}
                    >
                      <Button
                        type="link"
                        onClick={() => {
                          setIsEditEmailTemplateByEmailTemplateId(prev => {
                            const now = JSON.parse(JSON.stringify(prev));
                            now[emailTemplate._id] = false
                            return now
                          });
                        }}
                      >
                        <>
                          {t(EditEmailTemplatePageKeys.editEmailTemplate_cancelBtn)}
                        </>
                      </Button>
                      <Button
                        loading={isUpdatingEmailTemplateLoadingByEmailTemplateId[emailTemplate._id]}
                        type="primary"
                        onClick={() => {
                          setIsUpdatingEmailTemplateLoadingByEmailTemplateId(prev => {
                            const now = JSON.parse(JSON.stringify(prev));
                            now[emailTemplate._id] = true
                            return now
                          });

                          if (editorStateByEmailTemplateId[emailTemplate._id]) {
                            emailTemplate.content = editorStateByEmailTemplateId[emailTemplate._id]
                          }

                          const req: UpdateEmailTemplateRequest = {
                            emailTemplate: emailTemplate,
                          };

                          updateEmailTemplate(req)
                            .then((res) => {
                              if (!res.success) throw new Error(res.message);

                              message.success("Updated");
                              setIsEditEmailTemplateByEmailTemplateId(prev => {
                                const now = JSON.parse(JSON.stringify(prev));
                                now[emailTemplate._id] = false
                                return now
                              });

                              mutate(
                                `/emailTemplate/get/${context?.companyUser?.companyId}`
                              );
                            })
                            .catch((err) => message.error(err))
                            .finally(() => {
                              setIsUpdatingEmailTemplateLoadingByEmailTemplateId(prev => {
                                const now = JSON.parse(JSON.stringify(prev));
                                now[emailTemplate._id] = false
                                return now
                              })
                            });
                        }}
                        data-testid={`${TEST_IDS.SURVEY_CAMPAIGN_FORM.save_email_template_id}_${position}`}
                      >
                        {t(EditEmailTemplatePageKeys.editEmailTemplate_saveBtn)}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      maxHeight: 600,
                      overflow: "scroll",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: emailTemplate?.content || "",
                    }}
                  ></div>
                )}
              </Card>
            </div>
          );
        })
      }

      <Typography.Title level={3}>
        {t(SurveyCampaignFormKeys.surveyCampaignForm_faqString)}
      </Typography.Title>
      <Collapse>
        <Collapse.Panel
          header={t(SurveyCampaignFormKeys.surveyCampaignForm_whatIsLinkString)}
          key="1"
        >
          <p>{t(SurveyCampaignFormKeys.surveyCampaignForm_uniqueLinkString)}</p>
        </Collapse.Panel>
      </Collapse>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 10,
        }}
      >
        <Button onClick={(e) => {
          history.push(`/surveycampaign/edit/employees?surveyCampaignId=${getSurveyCampaignRes?.surveyCampaign._id}`)
        }}>
          {t(SurveyCampaignFormKeys.surveyCampaignForm_previewBackBtn)}
        </Button>
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            loading={isSaveAsDraftLoading}
            onClick={(e) => {
              setIsSaveAsDraftLoading(true);
              setTimeout(() => {
                message.success(`Survey campaign saved!`);
              }, 1 * 1000);
              setTimeout(() => {
                history.push(
                  `/surveycampaign?surveyCampaignId=${getSurveyCampaignRes?.surveyCampaign._id}`
                );
              }, 1 * 1000);
            }}
          >
            {t(SurveyCampaignFormKeys.surveyCampaignForm_previewDraftBtn)}
          </Button>
          <Button
            loading={isSendLoading}
            onClick={(e) => {
              Modal.success({
                title: t(
                  SurveyCampaignFormKeys.surveyCampaignForm_previewSendAreYouSurePrompt
                ),
                onOk: () => {
                  setIsSendLoading(true);

                  startSurveyCampaign(
                    getSurveyCampaignRes?.surveyCampaign._id || ""
                  )
                    .then(() => {
                      message.success(
                        t(
                          SurveyCampaignFormKeys.surveyCampaignForm_surveyCampaignStartedPrompt
                        )
                      );

                      setTimeout(() => {
                        history.push(
                          `/surveycampaign?surveyCampaignId=${getSurveyCampaignRes?.surveyCampaign._id}`
                        );
                      }, 1 * 1000);
                    })
                    .catch((err) => {
                      setIsSendLoading(false);
                      message.error(err.message);
                    });
                },
              });
            }}
            type="primary"
            disabled={isEditingAtLeastOneEmailTemplate}
          >
            {t(SurveyCampaignFormKeys.surveyCampaignForm_previewSendBtn)}
          </Button>
        </div>
      </div>
    </div>
  );
}
